import React, { forwardRef } from 'react';
import { formatDate } from '../../utils/dateUtils';

// Police import (une seule fois via style tag)
const FONT_TAG_ID = 'inv-fonts';
if (!document.getElementById(FONT_TAG_ID)) {
  const link = document.createElement('link');
  link.id   = FONT_TAG_ID;
  link.rel  = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap';
  document.head.appendChild(link);
}

/**
 * Carte d'invitation rendue live.
 * Utilise forwardRef pour que html2canvas puisse la capturer.
 */
const InvitationCard = forwardRef(function InvitationCard({
  design, plan, guestName, rsvpToken,
}, ref) {
  const d = design;
  const bg   = d.bg_color   || '#FAF6EE';
  const fg   = d.text_color || '#3D2F15';
  const theme = d.theme || 'elegant';

  const isModern  = theme === 'modern';
  const isBotanic = theme === 'botanic';
  const isMinimal = theme === 'minimal';

  const titleFont  = isModern ? "'Montserrat', sans-serif" : "'Playfair Display', serif";
  const bodyFont   = (isBotanic || !isModern) ? "'Cormorant Garamond', serif" : "'Montserrat', sans-serif";
  const cardShadow = isMinimal ? 'none' : '0 20px 60px rgba(0,0,0,.12)';
  const cardBorder = isMinimal ? `1px solid ${fg}30` : 'none';

  const eventDate = plan?.date_evenement
    ? formatDate(plan.date_evenement)
    : '';
  const hosts  = plan?.organisateur_nom || plan?.nom_evenement || '';
  const typeEv = plan?.type_evenement || 'mariage';
  const SUBTITLES = {
    mariage:             'vous invitent à célébrer leur union',
    anniversaire_adulte: 'vous invite à fêter cet anniversaire',
    anniversaire_enfant: 'vous invite à une fête d\'anniversaire',
    bapteme:             'vous invite à célébrer ce baptême',
    entreprise:          'vous invite à cet événement',
  };
  const subtitle = SUBTITLES[typeEv] || 'vous invite';

  const rsvpUrl = rsvpToken
    ? `${window.location.origin}/rsvp/${rsvpToken}`
    : null;

  return (
    <div
      ref={ref}
      style={{
        background:   bg,
        color:        fg,
        padding:      isMinimal ? '70px 48px' : '60px 40px',
        borderRadius: isMinimal ? 2 : 8,
        boxShadow:    cardShadow,
        border:       cardBorder,
        maxWidth:     600,
        width:        '100%',
        fontFamily:   bodyFont,
        textAlign:    'center',
        lineHeight:   1.7,
      }}
    >
      {/* Ornement haut */}
      {d.ornament && (
        <div style={{
          fontSize: 18, letterSpacing: '0.3em', opacity: 0.5,
          marginBottom: 20, fontFamily: bodyFont,
          color: fg,
        }}>{d.ornament}</div>
      )}

      {/* Hosts / Title */}
      <h1 style={{
        fontFamily: titleFont,
        fontSize:   isModern ? 26 : 34,
        fontWeight: 700,
        margin:     '0 0 8px',
        letterSpacing: isModern ? '0.08em' : '0.02em',
        textTransform: isModern ? 'uppercase' : 'none',
        lineHeight: 1.2,
        color: fg,
      }}>
        {hosts || plan?.nom_evenement || 'Votre événement'}
      </h1>

      {/* Sous-titre */}
      <p style={{
        fontSize:    17, fontStyle: isModern ? 'normal' : 'italic',
        opacity:     0.75, margin: '0 0 24px',
        fontFamily:  bodyFont,
        letterSpacing: isModern ? '0.05em' : '0',
        textTransform: isModern ? 'uppercase' : 'none',
      }}>
        {subtitle}
      </p>

      {/* Trait + Nom invité */}
      <div style={{ borderTop: `1px solid ${fg}50`, borderBottom: `1px solid ${fg}50`,
        padding: '12px 0', margin: '0 0 24px' }}>
        <span style={{
          fontFamily: titleFont, fontSize: 22, fontWeight: 400,
          letterSpacing: '0.04em',
        }}>
          — {guestName || 'Cher invité'} —
        </span>
      </div>

      {/* Message */}
      {d.message && (
        <p style={{
          fontSize: 15, fontStyle: 'italic', lineHeight: 1.8,
          maxWidth: 440, margin: '0 auto 24px',
          fontFamily: bodyFont,
        }}>{d.message}</p>
      )}

      {/* Date */}
      {eventDate && (
        <p style={{
          fontWeight: 700, fontSize: 16, letterSpacing: '0.05em',
          margin: '0 0 6px', fontFamily: titleFont,
        }}>{eventDate}</p>
      )}

      {/* Lieu */}
      {plan?.lieu && (
        <p style={{ fontSize: 14, fontStyle: 'italic', opacity: 0.7, margin: '0 0 20px' }}>
          {plan.lieu}
        </p>
      )}

      {/* Champs additionnels */}
      {(d.dress_code || d.rsvp_deadline || d.address || d.contact) && (
        <div style={{
          fontSize: 12, opacity: 0.75, lineHeight: 1.8, margin: '0 0 28px',
          fontFamily: bodyFont,
        }}>
          {d.dress_code    && <div>👔 {d.dress_code}</div>}
          {d.rsvp_deadline && <div>📅 Répondre avant le {d.rsvp_deadline}</div>}
          {d.address       && <div>📍 {d.address}</div>}
          {d.contact       && <div>📞 {d.contact}</div>}
        </div>
      )}

      {/* Bouton décoratif RSVP */}
      <div style={{
        display:       'inline-block',
        border:        `2px solid ${fg}`,
        borderRadius:  24,
        padding:       '8px 28px',
        fontSize:       11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        margin:        '0 0 16px',
        cursor:        'default',
        fontFamily:    titleFont,
      }}>
        Confirmer ma présence
      </div>

      {/* Lien RSVP */}
      {rsvpUrl && (
        <p style={{
          fontSize: 11, opacity: 0.45, fontFamily: 'monospace',
          wordBreak: 'break-all', margin: 0,
        }}>{rsvpUrl}</p>
      )}
    </div>
  );
});

export default InvitationCard;
